import React from 'react';

export interface RegistrationStep {
  num: number;
  label: string;
  icon: string;
}

/** Self and service-provider registration: email, then the OTP that verifies it. */
const DEFAULT_STEPS: RegistrationStep[] = [
  { num: 1, label: 'Interest', icon: 'bi-globe2' },
  { num: 2, label: 'Consent', icon: 'bi-shield-check' },
  { num: 3, label: 'Email', icon: 'bi-envelope' },
  { num: 4, label: 'Verify', icon: 'bi-key' },
  { num: 5, label: 'Details', icon: 'bi-person-badge' }
];

interface Props {
  currentStep: 1 | 2 | 3 | 4 | 5;
  title: string;
  maxWidth?: string;
  /** A flow with different steps (e.g. introduced registration, which has no OTP step). */
  steps?: RegistrationStep[];
}

export const RegistrationStepper: React.FC<Props> = ({ currentStep, title, maxWidth, steps = DEFAULT_STEPS }) => {
  return (
    <div 
      className="registration-stepper-card mx-auto" 
      style={maxWidth ? { maxWidth } : undefined}
    >
      <div className="gradient-header">
        {/* Left Side: Step Title */}
        <div className="step-info">
          <h2>{title}</h2>
          <p>Step {currentStep} of {steps.length}</p>
        </div>

        {/* Right Side: Stepper Progress */}
        <div className="stepper-progress-container">
          {steps.map((step, idx) => {
            const isDone = currentStep > step.num;
            const isCurrent = currentStep === step.num;

            return (
              <div key={step.num} className="stepper-step-node">
                {/* Connector Line */}
                {idx < steps.length - 1 && (
                  <div className="node-line">
                    <div className={`line-progress ${isDone ? 'done' : ''}`} />
                  </div>
                )}

                {/* Node Circle */}
                <div className={`node-circle ${isDone ? 'done' : isCurrent ? 'current' : ''}`}>
                  {isDone ? (
                    <i className="bi bi-check-lg done-check" />
                  ) : (
                    <i className={`bi ${step.icon} ${isCurrent ? 'current-icon' : ''}`} />
                  )}

                  {/* Active Pulse Ring */}
                  {isCurrent && <div className="pulse-ring" />}
                </div>

                {/* Label */}
                <span className={`step-label ${isDone ? 'done' : isCurrent ? 'current' : ''}`}>
                  {step.label}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

